import React, { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { fetchExperiments } from './fetchExperiments';
import { ExperimentContext } from './Context';
import { Data, Breakpoints, Allocation, JimdoApp } from './types';
import { filterData } from './utilities';
import { logger, LoggerTypes } from '../../utilities/logger/logger';
import { isMobileDevice, isMobileLayout } from '../../utilities';
import { isAffiliate as isAffiliateUser } from '../../utilities/isAffiliate/isAffiliate';
import { TrackingEvent } from '@jimdo/frontend-tracking';

interface ExperimentsProviderProps {
  children?: ReactNode;
  language: string;
  mobileBreakpoint?: Breakpoints;
  country?: string;
  track: (event: TrackingEvent) => void;
  jimdoApp: JimdoApp;
  dateJoined?: string;
  debugMode?: boolean;
  prefetchedAllocations?: Allocation[];
}

export const ExperimentsProvider: FC<PropsWithChildren<ExperimentsProviderProps>> = ({
  children,
  language,
  mobileBreakpoint,
  track,
  country,
  jimdoApp,
  dateJoined,
  debugMode,
  prefetchedAllocations,
}) => {
  const [allocations, setAllocations] = useState<Allocation[]>([]);

  const trackWithDefaults = ({ action, label, payload }: Omit<TrackingEvent, 'category'>) => {
    track({
      category: 'experiment', // debatable
      action,
      label,
      payload: {
        isMobileDevice: isMobileDevice(),
        isMobileLayout: isMobileLayout(mobileBreakpoint),
        ...payload,
      },
    });
  };

  const setUserAllocations = (allocations: Allocation[]) => {
    setAllocations(allocations);
    if (debugMode) {
      logger(LoggerTypes.INFO, allocations, jimdoApp);
    }
  };

  useEffect(() => {
    if (prefetchedAllocations === undefined) {
      const isAffiliate = isAffiliateUser();

      fetchExperiments({
        country,
        language,
        isMobileLayout: isMobileLayout(mobileBreakpoint),
        jimdoApp,
        isAffiliate,
        dateJoined,
      })
        .then((data: Data) => {
          if (!!data) {
            const filteredData = filterData(data) || [];
            setUserAllocations(filteredData);
          }
        })
        .catch((error) => {
          logger(LoggerTypes.ERROR, error, jimdoApp);
        });
    } else {
      setUserAllocations(prefetchedAllocations);
    }
  }, [language, isMobileLayout, country, jimdoApp, prefetchedAllocations]);

  return (
    <ExperimentContext.Provider
      value={{
        allocations,
        track: (payload) => trackWithDefaults(payload),
        jimdoApp,
        debugMode: !!debugMode,
      }}
    >
      {children}
    </ExperimentContext.Provider>
  );
};
